import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import { print } from 'graphql';
import {
  getFlowId,
  removeFlowId,
  setFlowId,
} from './useAdSubmissionFlow.helpers';
import { usePlatformSource } from '@/hooks/usePlatformSource';
import { usePageType } from '@/hooks/usePageType/usePageType';
import { PageType } from '@/hooks/usePageType/usePageType.types';
import { useNavigationStore } from '@/store/useNavigationStore';
import {
  AdSubmissionStatusEnum,
  CreateAdSubmissionResponse,
} from '@/api/types/ads.types';
import { CREATE_AD_SUBMISSION } from '@/api/mutations/ads.mutations';
import { makeUrlWithLang } from '@/utils/helpers';
import { CONFIG } from '@/constants/config';
import { useAdSubmissionFlowStore } from '@/store/useAdSubmissionFlowStore';

export const useAdSubmissionFlow = () => {
  const { i18n } = useTranslation();
  const source = usePlatformSource();
  const pageType = usePageType();
  const isClientNewAdPage =
    typeof window !== 'undefined' && pageType === PageType.NewAd;
  const navigationType = useNavigationStore(state => state.navigationType);
  const { events, asPath } = useRouter();

  const [createAdSubmission] =
    useMutation<CreateAdSubmissionResponse>(CREATE_AD_SUBMISSION);

  const sendAdSubmission = async (
    flowId: string,
    status: AdSubmissionStatusEnum,
    useKeepalive = false
  ) => {
    // Use fetch with keepalive for guaranteed delivery during page unload
    if (useKeepalive) {
      const graphqlEndpoint = makeUrlWithLang({
        protocol: CONFIG.protocol || '',
        host: CONFIG.host || '',
        path: '/graphql',
        lang: i18n.language,
      });

      fetch(graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: print(CREATE_AD_SUBMISSION),
          variables: { flowId, status, source },
        }),
        credentials: 'include',
        keepalive: true, // Ensures the request completes even during page navigation/close
      });
      return;
    }

    // Use Apollo Client mutation for normal requests
    const response = await createAdSubmission({
      variables: { flowId, status, source },
    });
    const returnedFlowId = response?.data?.createAdSubmission?.entity;

    // If this is a START request with empty flowId, save the returned flowId
    if (status === AdSubmissionStatusEnum.Start && !flowId && returnedFlowId) {
      setFlowId(String(returnedFlowId));
    }
  };

  // Initialize flow with START request
  const initializeFlow = () => {
    if (!getFlowId()) {
      // Send START status with empty flowId to get a new one from backend
      sendAdSubmission('', AdSubmissionStatusEnum.Start);
    }
  };

  // Handle page unload/close
  const onPageUnload = (clearFlowId = false) => {
    return () => {
      const flowId = getFlowId();

      const { isAdSubmitRedirect, setIsAdSubmitRedirect } =
        useAdSubmissionFlowStore.getState();

      if (isAdSubmitRedirect) {
        setIsAdSubmitRedirect(false);
      } else if (flowId) {
        // Use keepalive to ensure the request completes even during page navigation
        sendAdSubmission(flowId, AdSubmissionStatusEnum.Cancelled, true);
      }

      if (clearFlowId) {
        removeFlowId();
      }
    };
  };

  // Handle offline status change (network lost)
  const onOfflineStatusChange = () => {
    removeFlowId();
  };

  // Handle online status change (network restored)
  const onOnlineStatusChange = () => {
    initializeFlow();
  };

  // Handle reload - runs only once on mount
  useEffect(() => {
    const flowId = getFlowId();

    if (!flowId || !isClientNewAdPage) return;

    if (navigationType === 'reload') {
      sendAdSubmission(flowId, AdSubmissionStatusEnum.Reload);
    } else {
      removeFlowId();
    }
  }, []);

  // Setup event listeners - can run multiple times when dependencies change
  useEffect(() => {
    if (!isClientNewAdPage) return;

    initializeFlow();

    const pageHideHandler = onPageUnload();
    const routeChangeStartHandler = onPageUnload(true);

    window.addEventListener('pagehide', pageHideHandler);
    window.addEventListener('online', onOnlineStatusChange);
    window.addEventListener('offline', onOfflineStatusChange);
    events.on('routeChangeStart', routeChangeStartHandler);

    return () => {
      window.removeEventListener('pagehide', pageHideHandler);
      window.removeEventListener('online', onOnlineStatusChange);
      window.removeEventListener('offline', onOfflineStatusChange);
      events.off('routeChangeStart', routeChangeStartHandler);
    };
  }, [asPath, source]);
};
